import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import axios from 'axios';
import classNames from 'classnames';
import { Form, SubmitButton, Notice } from '../Form';
import Modal from '../Modal';
import { Row, Column } from '../Grid';
import schema from './schema';
import './styles.scss';

const Subscribe = ({ trigger, className, ...other }) => {
  const { site } = useStaticQuery(
    graphql`
      query SiteMetaData {
        site {
          siteMetadata {
            programName
            partnerPrivacyUrl
            subscribeFormId
          }
        }
      }
    `
  );

  const [isComplete, setIsComplete] = useState(false);
  const [errors, setErrors] = useState({});
  const [fieldValues, setFieldValues] = useState(
    Object.assign(
      {},
      ...Object.entries(schema).map(([k, v]) => ({
        [k]: 'value' in v ? v.value : 'checked' in v ? v.checked : null,
      }))
    )
  );

  if (typeof schema.subscribeToPartner.label === 'function') {
    schema.subscribeToPartner.label = schema.subscribeToPartner.label(
      site.siteMetadata.programName,
      site.siteMetadata.partnerPrivacyUrl
    );
  }

  const handleFieldChange = (name, value) => {
    setFieldValues((state) => ({
      ...state,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (!validateFields()) return;

    const formId = site.siteMetadata.subscribeFormId;

    if (formId) {
      axios.post(
        `https://cepdata.discoveryeducation.com/api/v2/form`,
        {
          property_id: formId,
          fields: fieldValues,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );
    }

    setIsComplete(true);
  };

  const getErrorMessage = (name, value) => {
    const { label, required, validation } = schema[name];

    if (
      required &&
      ((typeof schema[name].value && value === '') ||
        (Array.isArray(schema[name].value) && value.length === 0) ||
        (schema[name].value.constructor === Object &&
          Object.entries(value).length === 0))
    ) {
      return `${label} is required`;
    } else if (validation && !validation.test(value)) {
      return validation.message;
    }

    return false;
  };

  const validateFields = () => {
    const errors = {};

    Object.entries(fieldValues).forEach(([name, value]) => {
      const error = getErrorMessage(name, value);

      if (error) {
        errors[name] = error;
      }
    });

    setErrors(errors);

    if (Object.keys(errors).length) {
      return false;
    }

    return true;
  };

  const renderThankYou = () => (
    <>
      <h2 className="h3">Thank you for your interest in the program.</h2>
      <p>You will be notified of any updates and annoucements.</p>
    </>
  );

  const renderField = (name) => {
    const { component, validation, ...attributes } = schema[name];
    const FieldComponent = component;

    if ('value' in attributes) {
      attributes.value = fieldValues[name];
    } else if ('checked' in attributes) {
      attributes.checked = fieldValues[name];
    }

    return (
      <FieldComponent
        name={name}
        onChange={handleFieldChange}
        error={errors[name] ? errors[name] : undefined}
        {...attributes}
      />
    );
  };

  const renderForm = () => (
    <Form onSubmit={handleSubmit}>
      <h2>Sign Up For Updates</h2>
      {renderField('email')}
      <Row>
        <Column size={6}>
          <h3 className="subscribe__heading">Grade Band</h3>
          {renderField('inGradesKto3')}
          {renderField('inGrades4to5')}
          {renderField('inGrades6to8')}
          {renderField('inGrades9to12')}
        </Column>
        <Column size={6}>
          <h3 className="subscribe__heading">Interests</h3>
          {renderField('interestedInSTEM')}
          {renderField('interestedInLanguageArts')}
          {renderField('interestedInEducation')}
          {renderField('interestedInVirtualFieldTrips')}
          {renderField('interestedInSweepstakesAndChallenges')}
        </Column>
      </Row>
      {renderField('subscribeToDE')}
      {renderField('subscribeToPartner')}
      {Object.keys(errors).length > 0 && (
        <Notice error>Please review the form for errors.</Notice>
      )}
      <SubmitButton>Submit</SubmitButton>
    </Form>
  );

  return (
    <Modal trigger={trigger} title="Sign Up for Updates">
      <div className={classNames('subscribe', className)} {...other}>
        {isComplete ? renderThankYou() : renderForm()}
      </div>
    </Modal>
  );
};

Subscribe.propTypes = {
  /** The element that triggers the modal to open when clicked */
  trigger: PropTypes.node.isRequired,
  /** A custom class name */
  className: PropTypes.string,
};

export default Subscribe;
